import React, { useEffect, useMemo } from 'react';
import { useTranslations } from '@unisporkal/localization';
import {
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import { AssetSize, ImageSize } from 'types/isomorphic';
import useDownloadSize from '../../../../hooks/stateful/useDownloadSize';
import { trackRefinePanelDownloadSizeClick } from '../../../../support/ctaTracking';
import availableModifiedSizes from '../support/availableModifiedSizes';
import modifiedSizeCopy from '../support/modifiedSizeCopy';
import ModifiedImageRow from './ModifiedImageRow';
import styles from '../ModifiedBuyCard.module.scss';

interface ModifiedImagesProps {
  modifiedSizes: ImageSize[];
  originalSize?: AssetSize;
}

// Sort visible modified sizes by rank (Standard 0, Higher resolution 1,
// Enhanced detail 2). Grouping into bordered cards happens below; unmapped
// sizes sort to the front.
const orderByRank = (sizes: ImageSize[]): ImageSize[] =>
  [...sizes].sort(
    (a, b) =>
      (modifiedSizeCopy(a.sizeName)?.rank ?? 0) -
      (modifiedSizeCopy(b.sizeName)?.rank ?? 0)
  );

const ModifiedImages = ({
  modifiedSizes,
  originalSize = undefined,
}: ModifiedImagesProps) => {
  const t = useTranslations();
  const { downloadSize, setDownloadSize } = useDownloadSize();
  const visible: ImageSize[] = useMemo(
    () => availableModifiedSizes(modifiedSizes, originalSize),
    [modifiedSizes, originalSize]
  );

  // If the current selection was gated out (e.g. user switched to Small),
  // fall back to the first still-visible modified size.
  useEffect(() => {
    if (visible.length === 0) return;
    const stillVisible = visible.some(
      (s) => s.sizeName === downloadSize?.sizeName
    );
    if (!stillVisible) setDownloadSize(visible[0]);
  }, [visible, downloadSize, setDownloadSize]);

  if (visible.length === 0) return null;

  const ordered = orderByRank(visible);

  // Fire the slide-14 cta_interaction alongside the existing size selection.
  const handleSelect = (size: ImageSize) => {
    trackRefinePanelDownloadSizeClick(size.sizeName);
    setDownloadSize(size);
  };

  // Standard (1k, rank 0) and Higher resolution (4k, rank 1) share one bordered
  // card; Enhanced detail (2k, rank 2) sits in its own card below. Empty groups
  // (e.g. when gating leaves only the standard size) are dropped so a single
  // size renders as one card.
  const ENHANCED_RANK = 2;
  const isEnhanced = (size: ImageSize) =>
    modifiedSizeCopy(size.sizeName)?.rank === ENHANCED_RANK;
  const groups = [
    ordered.filter((size) => !isEnhanced(size)),
    ordered.filter(isEnhanced),
  ].filter((group) => group.length > 0);

  return (
    <section data-testid="modified-images">
      <Typography
        component={TypographyComponentEnum.DIV}
        variant={TypographyVariant.body.small}
        className={styles.sectionHeader}
      >
        {t('preshot_your_modified_image')}
      </Typography>
      {groups.map((group) => (
        <div
          key={group[0].sizeName}
          className={styles.optionsCard}
          data-testid="modified-images-group"
        >
          {group.map((size) => (
            <ModifiedImageRow
              key={size.sizeName}
              size={size}
              selected={downloadSize?.sizeName === size.sizeName}
              onSelect={handleSelect}
            />
          ))}
        </div>
      ))}
    </section>
  );
};

export default ModifiedImages;
